import { ChangeEventHandler } from 'react';
import clsx from 'clsx';
import {
  Box,
  ControllerAutocomplete,
  ControllerChipInput,
  ControllerMenuItem,
  ControllerSelect,
} from '@bymason-private/legacy-pattern-components';
import { LogLevel } from '@bymason/controller-api-hooks';

const defaultLevelList: LogLevel[] = ['info', 'warn', 'error', 'debug', 'verbose', 'fatal'];
const defaultBufferList = ['main'];

export function LogFilterForm({
  buffer,
  bufferList = defaultBufferList,
  className,
  disabled = false,
  formDirection = 'vertical',
  level,
  levelList = defaultLevelList,
  pid,
  pidList = [],
  tags,
  onFilterChange,
}: LogFilterFormProps) {
  const handleInputChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (evt) => {
    onFilterChange && onFilterChange(evt.target.name, evt.target.value);
  };

  const handlePidInputChange = (evt: unknown, selected: string | null) => {
    const newValue = selected || '';
    onFilterChange && onFilterChange('pid', newValue);
  };

  const handleTagInputChange = (tags: string[]) => {
    onFilterChange && onFilterChange('tags', tags);
  };

  return (
    <Box
      className={clsx(formDirection, className)}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        alignItems: 'center',
        gridGap: 12,
        width: '100%',
      }}
      component='form'
      autoComplete='off'
    >
      <ControllerSelect
        sx={{ margin: 0 }}
        placeholder='Log Level'
        name='level'
        value={level || ''}
        onChange={handleInputChange}
        disabled={disabled}
      >
        {['', ...levelList].map((lvl) => (
          <ControllerMenuItem key={lvl} value={lvl}>
            {lvl ? lvl : <em>None</em>}
          </ControllerMenuItem>
        ))}
      </ControllerSelect>
      <ControllerSelect
        sx={{ margin: 0 }}
        placeholder='Log Buffer'
        name='buffer'
        value={buffer || ''}
        onChange={handleInputChange}
        disabled={disabled}
      >
        {['', ...bufferList].map((buf) => (
          <ControllerMenuItem key={buf} value={buf}>
            {buf ? buf : <em>None</em>}
          </ControllerMenuItem>
        ))}
      </ControllerSelect>
      <ControllerAutocomplete<string>
        AutocompleteProps={{
          id: 'pid',
          sx: {
            gridColumn: '1 / 3',
            margin: 0,
          },
          freeSolo: true,
          options: pidList,
          inputValue: pid || '',
          onInputChange: handlePidInputChange,
        }}
        sx={{
          gridColumn: '1 / 3',
          margin: 0,
        }}
        placeholder='PID'
        disabled={disabled}
      />
      <ControllerChipInput
        sx={{
          gridColumn: '1 / 3',
          margin: 0,
        }}
        placeholder='Tags'
        name='tags'
        chips={tags || []}
        onChipsChange={handleTagInputChange}
        disabled={disabled}
      />
    </Box>
  );
}

interface LogFilterFormProps {
  className?: string;
  formDirection?: 'horizontal' | 'vertical';
  buffer?: string;
  bufferList?: string[];
  disabled?: boolean;
  level?: LogLevel;
  levelList?: LogLevel[];
  pid?: string;
  pidList?: string[];
  tags?: string[];
  onFilterChange?: (name: string, value: string | string[]) => void;
}
